import React, { useState, useEffect } from "react";
import {
  FaCalendarAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaLock,
  FaUnlock,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const FiscalYearManagement = () => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal Sheet View Controllers
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedFY, setSelectedFY] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    year: "",
    startDate: "",
    endDate: "",
    status: "OPEN",
  });

  // 1. Fetch Fiscal Years List
  const fetchFiscalYears = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("tenants/fiscal-years");
      setFiscalYears(resp.data || []);
    } catch (err) {
      setError(
        err?.message ||
          "Failed to synchronize runtime fiscal year configurations."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  // 2. Open Modal
  const openModal = (mode, fy = null) => {
    setModalMode(mode);
    setSelectedFY(fy);
    setError("");

    if (fy) {
      setFormData({
        id: fy.id,
        year: fy.year || "",
        startDate: fy.startDate ? fy.startDate.substring(0, 10) : "",
        endDate: fy.endDate ? fy.endDate.substring(0, 10) : "",
        status: fy.status || "OPEN",
      });
    } else {
      const currentYear = new Date().getFullYear();

      setFormData({
        id: null,
        year: currentYear,
        startDate: `${currentYear}-01-01`,
        endDate: `${currentYear}-12-31`,
        status: "OPEN",
      });
    }

    setIsModalOpen(true);
  };

  // 3. Save Fiscal Year
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError("Start date must be chronologically before the end date.");
      return;
    }

    const endpoint =
      modalMode === "create"
        ? "tenants/fiscal-years"
        : `tenants/fiscal-years/${formData.id}`;

    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          year: parseInt(formData.year, 10),
        },
      });

      fetchFiscalYears();
      setIsModalOpen(false);
    } catch (err) {
      setError(
        err?.message ||
          "Fiscal year schema failed database constraint validations."
      );
    }
  };

  // 4. Close Fiscal Year
  const handleCloseFiscalYear = async (year) => {
    const confirmation = window.confirm(
      `Are you sure you want to CLOSE the Fiscal Year ${year}?  Please review the implications: 
      \n Transfer the year's profit or loss to Retained Earnings
      \n Close all Revenue, COGS, and Expense accounts
      \n Prevent additional postings to the closed fiscal year
      \n Generate a Year-End Closing Journal`
    );

    if (!confirmation) return;

    setError("");
    setIsLoading(true);

    try {
      await apiRequest(`tenants/fiscal-years/${year}/close`, {
        method: "POST",
      });

      fetchFiscalYears();
    } catch (err) {
      setError(
        err?.message || "Execution engine blocked fiscal year closing sequence."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 5. Delete Fiscal Year
  const handleDelete = async (id, year, status) => {
    if (status === "CLOSED") {
      setError(
        "Compliance rules block deleting finalized, closed fiscal periods."
      );
      return;
    }

    if (
      !window.confirm(
        `Are you certain you want to permanently delete the Fiscal Year "${year}"?`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`tenants/fiscal-years/${id}`, {
        method: "DELETE",
      });

      fetchFiscalYears();
    } catch (err) {
      setError(
        err?.message || "Database engine rejected fiscal year drop command."
      );
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition font-mono dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-6 rounded-2xl backdrop-blur-md">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaCalendarAlt className="text-emerald-500" />
              Fiscal Year
            </h1>

            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Configure fiscal periods, close accounting cycles, and adjust
              retained earnings.
            </p>
          </div>

          <button
            onClick={() => openModal("create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
          >
            <FaPlus />
            New Fiscal Year
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Main Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                  <th className="px-6 py-4">Fiscal Year</th>
                  <th className="px-6 py-4">Duration</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-8 text-slate-500 dark:text-slate-500"
                    >
                      Querying ...
                    </td>
                  </tr>
                ) : fiscalYears.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-8 text-slate-500 dark:text-slate-500"
                    >
                      No active or closed fiscal years found.
                    </td>
                  </tr>
                ) : (
                  fiscalYears.map((fy) => (
                    <tr
                      key={fy.id}
                      className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                    >
                      {/* Fiscal Year */}
                      <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">
                        <span className="px-3 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-700 tracking-wide text-xs font-mono dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-200">
                          {fy.year}
                        </span>
                      </td>

                      {/* Duration */}
                      <td className="px-6 py-4 text-slate-600 font-mono dark:text-slate-300">
                        {fy.startDate} — {fy.endDate}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        {fy.status === "OPEN" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                            <FaUnlock className="text-[8px]" />
                            OPEN
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20">
                            <FaLock className="text-[8px]" />
                            CLOSED
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {fy.status === "OPEN" && (
                            <button
                              onClick={() => handleCloseFiscalYear(fy.year)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-xs transition dark:bg-amber-500/10 dark:hover:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/20"
                              title="Perform Year End Closing"
                            >
                              <FaCheckCircle className="text-xs" />
                              Close Year
                            </button>
                          )}

                          <button
                            onClick={() => openModal("view", fy)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-white/5"
                            title="View Details"
                          >
                            <FaEye />
                          </button>

                          <button
                            onClick={() => openModal("edit", fy)}
                            disabled={fy.status === "CLOSED"}
                            className="p-2 bg-blue-50 hover:bg-blue-100 disabled:opacity-30 disabled:hover:bg-blue-50 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:disabled:hover:bg-blue-600/10 dark:text-blue-400 dark:border-blue-500/10"
                            title="Modify Fiscal Settings"
                          >
                            <FaEdit />
                          </button>

                          <button
                            onClick={() =>
                              handleDelete(fy.id, fy.year, fy.status)
                            }
                            disabled={fy.status === "CLOSED"}
                            className="p-2 bg-red-50 hover:bg-red-100 disabled:opacity-30 disabled:hover:bg-red-50 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:disabled:hover:bg-red-600/10 dark:text-red-400 dark:border-red-500/10"
                            title="Delete Period"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Modal Drawer */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={() => setIsModalOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`relative w-full max-w-xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-2xl transition-transform duration-300 transform space-y-6 dark:bg-slate-900 dark:border-white/10 ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Drawer Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} Fiscal Year
              </h3>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            {/* Fiscal Year */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>Fiscal Year</label>

              <input
                type="number"
                required
                min="1900"
                max="2100"
                placeholder="e.g. 2026"
                disabled={
                  modalMode === "view" ||
                  (modalMode === "edit" && formData.status === "CLOSED")
                }
                value={formData.year}
                onChange={(e) =>
                  setFormData({ ...formData, year: e.target.value })
                }
                className={inputClass}
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Starts From</label>

                <input
                  type="date"
                  required
                  disabled={modalMode === "view"}
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startDate: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Ends On</label>

                <input
                  type="date"
                  required
                  disabled={modalMode === "view"}
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      endDate: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            {/* Status */}
            {(modalMode === "view" || modalMode === "edit") && (
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Status</label>

                <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs flex items-center gap-2 dark:bg-slate-950/40 dark:border-white/10">
                  {formData.status === "OPEN" ? (
                    <>
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping absolute" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 relative" />

                      <span className="text-emerald-700 dark:text-emerald-400 font-semibold tracking-wider">
                        OPEN RECORD (Modifiable)
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />

                      <span className="text-red-700 dark:text-red-400 font-semibold tracking-wider">
                        CLOSED BOOK (Finalized Archive)
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create" ? "Save" : "Apply Changes"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FiscalYearManagement;
